import React from 'react';
import { Bell, Calendar, Building2, Clock, CheckCircle, AlertTriangle, X, BellRing, Mail, MailX } from 'lucide-react';
import { format, isPast, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useThemeStore } from '../stores/themeStore';

export interface Alert {
  id: string;
  type: 'relance' | 'rendez-vous';
  company: string;
  date: Date;
  agency: string;
  status: 'pending' | 'completed' | 'late';
  step: number;
  description?: string;
  action?: string;
  emailSent?: boolean;
  emailSentAt?: Date;
  emailError?: string;
}

interface AlertsPanelProps {
  alerts: Alert[];
  onUpdateStatus?: (alertId: string, status: Alert['status']) => void;
  onDeleteAlert?: (alertId: string) => void;
}

function AlertsPanel({ alerts, onUpdateStatus, onDeleteAlert }: AlertsPanelProps) {
  const [statusFilter, setStatusFilter] = React.useState<'all' | 'pending' | 'late'>('all');
  const { darkMode } = useThemeStore();
  const isReadOnly = !onUpdateStatus;

  const getAlertStatus = (alert: Alert) => {
    if (alert.status === 'completed') return 'completed';
    if (alert.status === 'late') return 'late';
    if (isPast(alert.date)) return 'late';
    return 'pending';
  };

  // Filtrer d'abord par statut, puis trier
  const filteredAndSortedAlerts = [...alerts]
    .filter(alert => {
      const status = getAlertStatus(alert);
      if (statusFilter === 'all') return true;
      return status === statusFilter;
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const getStepLabel = (step: number) => {
    switch (step) {
      case 1:
        return 'Premier contact - Présentation Anthea';
      case 2:
        return 'Newsletter - Profils disponibles';
      case 3:
        return 'Newsletter - Candidatures multi-agences';
      default:
        return 'Relance';
    }
  };

  const getStatusConfig = (status: string) => {
    if (darkMode) {
      switch (status) {
        case 'completed':
          return {
            border: 'border-green-800',
            bg: 'bg-green-900',
            icon: <CheckCircle className="w-4 h-4 text-green-400" />,
            text: 'Effectué'
          };
        case 'late':
          return {
            border: 'border-red-800',
            bg: 'bg-red-900',
            icon: <AlertTriangle className="w-4 h-4 text-red-400" />,
            text: 'En retard'
          };
        default:
          return {
            border: 'border-orange-800',
            bg: 'bg-orange-900',
            icon: <Clock className="w-4 h-4 text-orange-400" />,
            text: 'En attente'
          };
      }
    } else {
      switch (status) {
        case 'completed':
          return {
            border: 'border-green-100',
            bg: 'bg-green-50',
            icon: <CheckCircle className="w-4 h-4 text-green-600" />,
            text: 'Effectué'
          };
        case 'late':
          return {
            border: 'border-red-100',
            bg: 'bg-red-50',
            icon: <AlertTriangle className="w-4 h-4 text-red-600" />,
            text: 'En retard'
          };
        default:
          return {
            border: 'border-orange-100',
            bg: 'bg-orange-50',
            icon: <Clock className="w-4 h-4 text-orange-600" />,
            text: 'En attente'
          };
      }
    }
  };

  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Bell className="w-5 h-5 mr-2 text-blue-600" />
            Alertes de relance
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
              className={`px-2.5 py-1 rounded-full text-sm transition-all duration-200 ${
                statusFilter === 'pending'
                  ? 'bg-orange-600 text-white shadow-lg scale-105'
                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200 hover:scale-105'
              }`}
            >
              {alerts.filter(a => getAlertStatus(a) === 'pending').length} en attente
            </button>
            <button
              onClick={() => setStatusFilter(statusFilter === 'late' ? 'all' : 'late')}
              className={`px-2.5 py-1 rounded-full text-sm flex items-center transition-all duration-200 ${
                statusFilter === 'late'
                  ? 'bg-red-600 text-white shadow-lg scale-105'
                  : 'bg-red-100 text-red-700 hover:bg-red-200 hover:scale-105'
              }`}
            >
              {alerts.filter(a => getAlertStatus(a) === 'late').length} en retard
              {alerts.filter(a => getAlertStatus(a) === 'late').length > 0 && (
                <BellRing className={`w-4 h-4 ml-1 animate-pulse ${statusFilter === 'late' ? 'text-white' : 'text-red-600'}`} />
              )}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {filteredAndSortedAlerts.map((alert) => {
            const status = getAlertStatus(alert);
            const statusConfig = getStatusConfig(status);

            return (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border ${statusConfig.border} ${statusConfig.bg} relative`}
              >
                {status === 'completed' && onDeleteAlert && (
                  <button
                    onClick={() => onDeleteAlert(alert.id)}
                    className={`absolute top-2 right-2 p-1 rounded-full ${
                      darkMode 
                        ? 'hover:bg-red-800 text-red-300' 
                        : 'hover:bg-red-100 text-red-500'
                    }`}
                    title="Supprimer l'alerte"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Building2 className={`w-4 h-4 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} />
                      <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{alert.company}</h3>
                    </div>
                    <div className={`mt-1 flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <Clock className="w-4 h-4" />
                      <span>{format(alert.date, 'd MMMM yyyy', { locale: fr })}</span>
                    </div>
                    <div className={`mt-1 flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <Calendar className="w-4 h-4" />
                      <span>{alert.type === 'relance' ? getStepLabel(alert.step) : 'Rendez-vous'}</span>
                    </div>
                    {alert.description && (
                      <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{alert.description}</p>
                    )}
                    {alert.action && (
                      <p className={`mt-1 text-sm font-medium ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{alert.action}</p>
                    )}
                  </div>

                  {!isReadOnly && status !== 'completed' && (
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => onUpdateStatus(alert.id, 'completed')}
                        className={`px-3 py-1.5 text-sm ${darkMode ? 'bg-gray-700 text-green-400 border border-green-700 hover:bg-green-900' : 'bg-white text-green-600 border border-green-200 hover:bg-green-50'} rounded-lg`}
                      >
                        Marquer effectué
                      </button>
                      {status === 'pending' && (
                        <button
                          onClick={() => onUpdateStatus(alert.id, 'late')}
                          className={`px-3 py-1.5 text-sm ${darkMode ? 'bg-gray-700 text-red-400 border border-red-700 hover:bg-red-900' : 'bg-white text-red-600 border border-red-200 hover:bg-red-50'} rounded-lg`}
                        >
                          Marquer en retard
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-2 text-sm flex-wrap">
                  {statusConfig.icon}
                  <span className={status === 'completed' ? (darkMode ? 'text-green-400' : 'text-green-600') :
                          status === 'late' ? (darkMode ? 'text-red-400' : 'text-red-600') :
                          (darkMode ? 'text-orange-400' : 'text-orange-600')}>
                    {statusConfig.text}
                  </span>
                  {status === 'late' && (
                    <BellRing className={`w-4 h-4 ml-2 animate-pulse ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                  )}

                  {alert.emailSent && (
                    <div className={`ml-2 flex items-center gap-1 px-2 py-1 rounded ${
                      darkMode ? 'bg-green-900 text-green-300' : 'bg-green-50 text-green-700'
                    }`}>
                      <Mail className="w-3 h-3" />
                      <span className="text-xs">
                        Email envoyé {alert.emailSentAt && `le ${format(alert.emailSentAt, 'dd/MM/yy à HH:mm', { locale: fr })}`}
                      </span>
                    </div>
                  )}

                  {alert.emailError && (
                    <div className={`ml-2 flex items-center gap-1 px-2 py-1 rounded ${
                      darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-700'
                    }`}>
                      <MailX className="w-3 h-3" />
                      <span className="text-xs" title={alert.emailError}>
                        Erreur d'envoi
                      </span>
                    </div>
                  )}

                  {!alert.emailSent && !alert.emailError && alert.type === 'rendez-vous' && (
                    <div className={`ml-2 flex items-center gap-1 px-2 py-1 rounded ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
                    }`}>
                      <Mail className="w-3 h-3" />
                      <span className="text-xs">Email non envoyé</span>
                    </div>
                  )}
                </div>

                {alert.step === 1 && status !== 'completed' && (
                  <div className={`mt-3 p-3 ${darkMode ? 'bg-gray-700' : 'bg-white'} rounded-lg text-sm`}>
                    <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Actions suggérées :</p>
                    <ul className={`mt-1 space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <li>• Présenter les services Anthea</li>
                      <li>• Proposer un rendez-vous de découverte</li>
                      <li>• Envoyer la documentation par email</li>
                    </ul>
                  </div>
                )}
              </div>
            );
          })}

          {filteredAndSortedAlerts.length === 0 && alerts.length > 0 && (
            <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'} py-4`}>
              Aucune alerte {statusFilter === 'pending' ? 'en attente' : statusFilter === 'late' ? 'en retard' : ''} pour le moment
            </p>
          )}

          {alerts.length === 0 && (
            <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'} py-4`}>
              Aucune alerte de relance pour le moment
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AlertsPanel;